import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Button, Tag, Space, Popconfirm, message, Modal, Avatar, Form, Input, InputNumber, Select, DatePicker } from 'antd';
import { PlusOutlined, CheckOutlined, CloseOutlined, HistoryOutlined, UserOutlined, EditOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { apiClient } from '../../lib/api-client';
import dayjs from 'dayjs';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';

interface PromoCode {
  id: number;
  code: string;
  type: 'PERCENTAGE' | 'FIXED_AMOUNT';
  value: number;
  maxUsages: number | null;
  currentUsages: number;
  isActive: boolean;
  startsAt: string | null;
  expiresAt: string | null;
}

interface PromoCodeUsage {
  id: number;
  createdAt: string;
  discountAmount: number;
  user: {
    id: number;
    fullName: string;
    phone: string;
  };
}

interface UsageHistoryData {
  promoCode: PromoCode;
  usages: PromoCodeUsage[];
  total: number;
}

export const PromoCodeList = () => {
  const { create } = useNavigation();
  const { can } = usePermission();
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [usageData, setUsageData] = useState<UsageHistoryData | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm] = Form.useForm();

  const { tableProps, tableQueryResult } = useTable<PromoCode>({
    resource: 'admin/promo-codes',
    syncWithLocation: true,
  });

  const handleToggle = async (id: number) => {
    try {
      await apiClient.put(`/admin/promo-codes/${id}/toggle`);
      message.success(t('common.success'));
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleEdit = (record: PromoCode) => {
    setEditingPromo(record);
    editForm.setFieldsValue({
      code: record.code,
      type: record.type,
      value: record.value,
      maxUsages: record.maxUsages,
      minTripAmount: (record as any).minTripAmount ?? undefined,
      maxDiscount: (record as any).maxDiscount ?? undefined,
      startsAt: record.startsAt ? dayjs(record.startsAt) : undefined,
      expiresAt: record.expiresAt ? dayjs(record.expiresAt) : undefined,
    });
    setEditModalOpen(true);
  };

  const handleEditSave = async (values: any) => {
    if (!editingPromo) return;
    setEditSaving(true);
    try {
      const payload: any = {
        type: values.type,
        value: values.value,
        maxUsages: values.maxUsages ?? null,
        minTripAmount: values.minTripAmount ?? null,
        maxDiscount: values.maxDiscount ?? null,
        startsAt: values.startsAt ? values.startsAt.toISOString() : null,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
      };
      await apiClient.put(`/admin/promo-codes/${editingPromo.id}`, payload);
      message.success(t('common.success'));
      setEditModalOpen(false);
      setEditingPromo(null);
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleViewUsage = async (promoCode: PromoCode) => {
    setLoadingUsage(true);
    setUsageModalOpen(true);
    try {
      const response = await apiClient.get(`/admin/promo-codes/${promoCode.id}/usage`);
      setUsageData(response.data);
    } catch {
      message.error('Ishlatilish tarixini yuklashda xatolik');
      setUsageModalOpen(false);
    } finally {
      setLoadingUsage(false);
    }
  };

  const formatValue = (record: PromoCode) => {
    if (record.type === 'PERCENTAGE') {
      return `${record.value}%`;
    }
    return new Intl.NumberFormat('uz-UZ').format(record.value) + ' so\'m';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
  };

  const usageColumns = [
    {
      title: t('table.user'),
      key: 'user',
      render: (_: unknown, record: PromoCodeUsage) => (
        <Space>
          <Avatar icon={<UserOutlined />} size="small" />
          <div>
            <div>{record.user?.fullName || 'Ismi yo\'q'}</div>
            <div style={{ fontSize: 12, color: '#7F8C8D' }}>{record.user?.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: t('promo.discount'),
      dataIndex: 'discountAmount',
      key: 'discountAmount',
      render: (value: number) => formatCurrency(value || 0),
    },
    {
      title: t('promo.usedAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString('uz-UZ'),
    },
  ];

  const columns = [
    {
      title: t('promo.code'),
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <strong>{code}</strong>,
    },
    {
      title: t('promo.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'PERCENTAGE' ? 'blue' : 'green'}>
          {type === 'PERCENTAGE' ? t('promo.percentage') : t('promo.fixedAmount')}
        </Tag>
      ),
    },
    {
      title: t('promo.value'),
      key: 'value',
      render: (_: unknown, record: PromoCode) => formatValue(record),
    },
    {
      title: t('promo.usage'),
      key: 'usage',
      render: (_: unknown, record: PromoCode) => (
        <Button
          type="link"
          size="small"
          onClick={() => handleViewUsage(record)}
          style={{ padding: 0 }}
        >
          {record.currentUsages}{record.maxUsages ? ` / ${record.maxUsages}` : ''}
        </Button>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'default'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('promo.validity'),
      key: 'dates',
      render: (_: unknown, record: PromoCode) => (
        <div style={{ fontSize: 12 }}>
          {record.startsAt && (
            <div>Boshlanish: {new Date(record.startsAt).toLocaleDateString('uz-UZ')}</div>
          )}
          {record.expiresAt && (
            <div>Tugash: {new Date(record.expiresAt).toLocaleDateString('uz-UZ')}</div>
          )}
          {!record.startsAt && !record.expiresAt && '-'}
        </div>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: PromoCode) => (
        <Space>
          {can('promo', 'update') && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              title="Tahrirlash"
            />
          )}
          <Button
            type="text"
            icon={<HistoryOutlined />}
            onClick={() => handleViewUsage(record)}
            title="Ishlatilish tarixi"
          />
          {can('promo', 'update') && (
            <Popconfirm
              title={record.isActive ? 'O\'chirishni tasdiqlaysizmi?' : 'Yoqishni tasdiqlaysizmi?'}
              onConfirm={() => handleToggle(record.id)}
            >
              <Button
                type="text"
                icon={record.isActive ? <CloseOutlined /> : <CheckOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        title={t('promo.title')}
        headerButtons={
          can('promo', 'create') ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => create('taxi/promo-codes')}
            >
              {t('promo.newPromo')}
            </Button>
          ) : null
        }
      >
        <Table {...tableProps} columns={columns} rowKey="id" />
      </List>

      {/* Edit Modal */}
      <Modal
        title={t('promo.editPromo')}
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingPromo(null);
        }}
        footer={null}
        width={500}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSave}>
          <Form.Item name="code" label="Kod">
            <Input disabled />
          </Form.Item>
          <Form.Item name="type" label="Turi" rules={[{ required: true, message: 'Turni tanlang' }]}>
            <Select
              options={[
                { value: 'PERCENTAGE', label: 'Foiz' },
                { value: 'FIXED_AMOUNT', label: "Qat'iy summa" },
              ]}
            />
          </Form.Item>
          <Form.Item name="value" label="Qiymat" rules={[{ required: true, message: 'Qiymatni kiriting' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="maxUsages" label="Maksimal ishlatishlar">
            <InputNumber min={1} style={{ width: '100%' }} placeholder="Cheksiz" />
          </Form.Item>
          <Form.Item name="minTripAmount" label="Minimal sayohat summasi">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Ixtiyoriy" />
          </Form.Item>
          <Form.Item name="maxDiscount" label="Maksimal chegirma">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Ixtiyoriy" />
          </Form.Item>
          <Form.Item name="startsAt" label="Boshlanish sanasi">
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="expiresAt" label="Tugash sanasi">
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditModalOpen(false)}>{t('common.cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={editSaving}>{t('common.save')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Usage History Modal */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            <span>Ishlatilish tarixi: {usageData?.promoCode?.code}</span>
          </Space>
        }
        open={usageModalOpen}
        onCancel={() => {
          setUsageModalOpen(false);
          setUsageData(null);
        }}
        footer={null}
        width={700}
      >
        {usageData && (
          <div>
            <div style={{ marginBottom: 16, padding: 16, background: '#F5F7FA', borderRadius: 8 }}>
              <Space size="large">
                <div>
                  <div style={{ color: '#7F8C8D', fontSize: 12 }}>Turi</div>
                  <Tag color={usageData.promoCode.type === 'PERCENTAGE' ? 'blue' : 'green'}>
                    {usageData.promoCode.type === 'PERCENTAGE' ? 'Foiz' : 'Qat\'iy summa'}
                  </Tag>
                </div>
                <div>
                  <div style={{ color: '#7F8C8D', fontSize: 12 }}>Qiymat</div>
                  <strong>{formatValue(usageData.promoCode)}</strong>
                </div>
                <div>
                  <div style={{ color: '#7F8C8D', fontSize: 12 }}>Jami ishlatilgan</div>
                  <strong>{usageData.total}</strong>
                </div>
              </Space>
            </div>

            {usageData.usages.length > 0 ? (
              <Table
                dataSource={usageData.usages}
                columns={usageColumns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="small"
                loading={loadingUsage}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#7F8C8D' }}>
                Bu promo kod hali ishlatilmagan
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};
